import React, { useCallback, useState } from 'react';
import { Building2, Clock, DollarSign, Download } from 'lucide-react';
import { useVoiceTenantCallAggregates } from './useVoiceCalls';
import { fetchVoiceCallAggregates } from './voice-calls-api';
import { buildVoiceTenantCsvLines, downloadCsv, getVoiceCsvFilename } from './voice-csv-utils';
import { daysToDateRange, formatCost, formatDuration } from './voice-utils';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { DateRangePicker } from '../../components/ui/date-range-picker';
import { useToast } from '../../hooks/useToast';
import { PageHeader } from '../../components/common/PageHeader';
import { VoiceTenantsTable } from './VoiceTenantsTable';

export const VoicePage: React.FC = () => {
  const [selectedDays, setSelectedDays] = useState<number>(0);
  const [isExporting, setIsExporting] = useState(false);
  const { addToast } = useToast();

  const {
    data,
    isLoading,
    isFetching,
    error,
    hasNextPage,
    currentPage,
    goToNextPage,
    goToPreviousPage,
  } = useVoiceTenantCallAggregates(selectedDays);

  const activeTenants = data.length;
  const totalDurationSecs = data.reduce((sum, t) => sum + (t.totalDurationSecs ?? 0), 0);
  const totalCostCents = data.reduce((sum, t) => sum + t.totalCostCents, 0);

  const periodLabel = selectedDays === 0 ? 'All Time' : `Last ${selectedDays}d`;

  const handleDownloadCsv = useCallback(async () => {
    setIsExporting(true);
    try {
      const dateRange = daysToDateRange(selectedDays);
      let cursor: string | undefined;
      let hasNextPage = true;
      const csvLines: string[] = [];
      const filename = getVoiceCsvFilename(selectedDays);

      while (hasNextPage) {
        const response = await fetchVoiceCallAggregates({
          cursor,
          limit: 100,
          ...dateRange,
        });

        const pageCsvLines = buildVoiceTenantCsvLines(response.data);
        if (csvLines.length === 0) {
          csvLines.push(...pageCsvLines);
        } else {
          csvLines.push(...pageCsvLines.slice(1));
        }

        hasNextPage = response.hasNextPage;
        cursor = response.cursor;
      }

      downloadCsv(filename, csvLines);
    } catch (error) {
      console.error('Failed to export Assist CSV', error);
      addToast({
        variant: 'destructive',
        title: 'Failed to export CSV',
        description:
          error instanceof Error
            ? error.message
            : 'Something went wrong while exporting Assist usage data.',
      });
    } finally {
      setIsExporting(false);
    }
  }, [addToast, selectedDays]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Assist"
        description="Call statistics across all tenants"
        right={
          <div className="flex items-center gap-2">
            <DateRangePicker
              selectedDays={selectedDays}
              onSelectDays={setSelectedDays}
              isLoading={isFetching}
            />
            <Button
              variant="ghost"
              onClick={() => {
                void handleDownloadCsv();
              }}
              disabled={isLoading || isFetching || isExporting}>
              <Download className="h-4 w-4 mr-2" />
              {isExporting ? 'Exporting...' : 'Download CSV'}
            </Button>
          </div>
        }
      />

      {/* Summary Cards */}
      <div
        className={`grid grid-cols-1 md:grid-cols-3 gap-4 transition-opacity duration-200 ${isFetching ? 'opacity-75' : 'opacity-100'}`}>
        <Card
          className={`p-6 transition-all duration-300 ${isFetching ? 'opacity-75 scale-[0.98]' : 'opacity-100 scale-100'}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Assist Tenants ({periodLabel})</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">{activeTenants}</p>
            </div>
            <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Building2 className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </Card>
        <Card
          className={`p-6 transition-all duration-300 ${isFetching ? 'opacity-75 scale-[0.98]' : 'opacity-100 scale-100'}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Time ({periodLabel})</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">
                {formatDuration(totalDurationSecs)}
              </p>
            </div>
            <div className="h-12 w-12 bg-amber-100 rounded-lg flex items-center justify-center">
              <Clock className="h-6 w-6 text-amber-600" />
            </div>
          </div>
        </Card>
        <Card
          className={`p-6 transition-all duration-300 ${isFetching ? 'opacity-75 scale-[0.98]' : 'opacity-100 scale-100'}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Usage Cost ({periodLabel})</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">{formatCost(totalCostCents)}</p>
            </div>
            <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
              <DollarSign className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </Card>
      </div>

      <VoiceTenantsTable
        data={data}
        isLoading={isLoading}
        isFetching={isFetching}
        error={error}
        hasNextPage={hasNextPage}
        currentPage={currentPage}
        onNextPage={goToNextPage}
        onPreviousPage={goToPreviousPage}
      />
    </div>
  );
};
